import React, { useState } from 'react';

import { useQuery } from '@tanstack/react-query';
import { useSelector } from 'react-redux';

import {
  Alert,
  Box,
  Link,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';

import { useGetUser } from '~/hooks/apis/users/useGetUser';

import { getPurchases } from '~/services/OrdersService';

interface Props {
  userId: string;
}

const RECENT_DAYS = 45;
const MIN_FRAUD_SCORE = 3;

export default function FraudRiskAlert({ userId }: Props) {
  const tenant = useSelector((state: App.State) => state.tenant);
  const [showDetails, setShowDetails] = useState(false);

  // First, fetch the user to get id_member
  const { data: user } = useGetUser({ userId, brand: tenant.brand });

  // Then fetch orders for this user
  const { data: purchasesResponse } = useQuery({
    queryKey: ['purchases', 'fraud-check', user?.id_member, tenant.brand],
    queryFn: async () => {
      if (!user?.id_member) {
        return { result: [] };
      }

      // Fetch a reasonable number of recent orders
      return await getPurchases({
        page: 1,
        per_page: 5,
        brand: tenant.brand,
        customer_id: user.id_member,
      });
    },
    enabled: !!user?.id_member,
  });

  // Filter for recent high-scoring fraud orders
  if (!purchasesResponse?.result) {
    return null;
  }

  const now = new Date();
  const recentThreshold = new Date(now.getTime() - RECENT_DAYS * 24 * 60 * 60 * 1000);

  const recentFraudOrders = purchasesResponse.result.filter((order: App.Order) => {
    const orderDate = new Date(order.created_at);
    const isRecent = orderDate >= recentThreshold;
    const hasHighFraudScore =
      order.fraud_score !== undefined && order.fraud_score !== null && order.fraud_score >= MIN_FRAUD_SCORE;

    return isRecent && hasHighFraudScore;
  });

  // Render null if no recent high-scoring fraud orders
  if (!recentFraudOrders || recentFraudOrders.length === 0) {
    return null;
  }

  // Find the highest fraud score
  const fraudScores = recentFraudOrders
    .map((order: App.Order) => order.fraud_score)
    .filter((score): score is number => score !== undefined && score !== null);
  const maxFraudScore = Math.max(...fraudScores);

  // Determine severity and message based on highest fraud score
  const isHighRisk = maxFraudScore >= 4;
  const severity = isHighRisk ? 'error' : 'warning';
  const riskLevel = isHighRisk ? 'high' : 'low-to-moderate';

  // Sort orders by date (most recent first)
  const sortedOrders = [...recentFraudOrders].sort((a, b) => {
    return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
  });

  // Render warning/error if there are recent high-scoring fraud orders
  return (
    <Box sx={{ mb: 4 }}>
      <Alert severity={severity}>
        <strong>Fraud Warning:</strong> This user has recent orders with {riskLevel} fraud risk (within the last{' '}
        {RECENT_DAYS} days).
        <br />
        {isHighRisk ? (
          <>Consult a Team Leader before making further bookings.</>
        ) : (
          <>
            Consider consulting a Team Leader before making further bookings, unless you're confident you understand the
            risks.
          </>
        )}
        <br />
        Check the{' '}
        <Link href={`/users/${userId}/history`} underline="hover" color="primary" fontWeight={500}>
          Fraud History
        </Link>{' '}
        for more details.
        <br />
        <Link
          component="button"
          onClick={() => setShowDetails(!showDetails)}
          underline="hover"
          sx={{ cursor: 'pointer', mt: 1 }}
        >
          {showDetails ? 'less' : 'more'}
        </Link>
        {showDetails && (
          <Box sx={{ mt: 2 }}>
            <TableContainer component={Paper}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>
                      <Typography variant="subtitle2" fontWeight="bold">
                        Date
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="subtitle2" fontWeight="bold">
                        Fraud Risk
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="subtitle2" fontWeight="bold">
                        Fraud Details
                      </Typography>
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sortedOrders.map((order: App.Order) => (
                    <TableRow key={order.id}>
                      <TableCell>
                        {new Date(order.created_at).toLocaleString('en-AU', {
                          year: 'numeric',
                          month: 'short',
                          day: '2-digit',
                          hour: '2-digit',
                          minute: '2-digit',
                        })}
                      </TableCell>
                      <TableCell>
                        <Typography
                          variant="body2"
                          fontWeight={order.fraud_score && order.fraud_score >= 4 ? 'bold' : 'normal'}
                        >
                          {order.fraud_score ?? 'N/A'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{order.fraud_desc || 'No details available'}</Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}
      </Alert>
    </Box>
  );
}
